// Inline HTML for the Founders+ product preview loop.
// Used by AuthShowcasePanel (login/register page) and DemoModeTour (post-login overlay).
//
// We render this via <iframe srcDoc={...}> instead of loading a static file
// so the preview never depends on a network request, server config, or
// `X-Frame-Options` headers from the host. about:srcdoc is always same-origin
// and cannot be blocked by edge proxies / CDNs.

const productPreviewHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>Founders+ Product Loop</title>
<style>
  :root {
    --bg: #0a0f14;
    --surface: #111820;
    --panel: #0f1724;
    --text: #e6edf6;
    --muted: #8ea0b4;
    --line: rgba(255,255,255,0.09);
    --line-strong: rgba(255,255,255,0.14);
    --accent: #2dd4bf;
    --accent-soft: rgba(45,212,191,0.15);
    --warn: #f59e0b;
    --danger: #f87171;
    --good: #34d399;
    --indigo: #a5b4fc;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    width: 100%; height: 100%; overflow: hidden;
    background: var(--bg); color: var(--text);
    font-family: "Inter","Segoe UI",Tahoma,sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  .shell { height: 100%; display: grid; grid-template-columns: 64px 1fr; }

  .side {
    border-right: 1px solid var(--line);
    background: #0c131d;
    display: flex; flex-direction: column;
    align-items: center; padding: 14px 0; gap: 8px;
  }
  .logo {
    width: 34px; height: 34px; border-radius: 10px;
    background: linear-gradient(135deg,#37e8d6,#18a798);
    color: #04211e; font-size: 14px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
  }
  .nav-item {
    width: 46px; padding: 7px 2px; border-radius: 10px;
    text-align: center; color: var(--muted);
    font-size: 9.5px; line-height: 1.2; letter-spacing: 0.02em;
    border: 1px solid transparent; transition: all 0.35s ease;
    margin-top: 2px;
  }
  .nav-item.is-active {
    color: var(--accent);
    border-color: rgba(45,212,191,0.26);
    background: rgba(45,212,191,0.08);
  }

  .content { padding: 14px; height: 100%; }
  .frame {
    height: 100%;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: linear-gradient(180deg,#0f1724 0%,#0c141f 100%);
    overflow: hidden;
    position: relative;
  }
  .view {
    position: absolute; inset: 0;
    padding: 16px 18px 110px;
    overflow: hidden;
    opacity: 0; pointer-events: none;
    transform: translateY(6px);
    transition: opacity 480ms ease, transform 480ms ease;
  }
  .view.is-active { opacity: 1; transform: translateY(0); }

  .top {
    display: flex; justify-content: space-between; align-items: flex-end;
    gap: 12px; padding-bottom: 12px;
    border-bottom: 1px solid var(--line);
    margin-bottom: 14px;
  }
  .title { font-size: 24px; letter-spacing: -0.02em; line-height: 1.05; font-weight: 600; }
  .sub { margin-top: 4px; color: var(--muted); font-size: 12px; }
  .chip {
    border: 1px solid rgba(45,212,191,0.32);
    background: rgba(45,212,191,0.11);
    color: #7ff5e7; font-size: 11px;
    border-radius: 999px; padding: 5px 10px; white-space: nowrap;
  }

  .metrics {
    display: grid; grid-template-columns: repeat(4, minmax(0,1fr));
    gap: 10px; margin-bottom: 14px;
  }
  .metric {
    border: 1px solid var(--line); background: #111a28;
    border-radius: 10px; padding: 10px 11px; min-height: 60px;
  }
  .metric-k { color: var(--muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; }
  .metric-v { margin-top: 4px; font-size: 22px; font-weight: 700; letter-spacing: -0.02em; }
  .metric-trend { margin-top: 2px; font-size: 10px; color: var(--good); }

  .section-h {
    font-size: 11px; color: var(--muted);
    text-transform: uppercase; letter-spacing: 0.1em;
    margin: 14px 0 8px;
  }
  .card {
    border: 1px solid var(--line); border-radius: 12px;
    background: #111a28; padding: 13px 14px;
  }
  .card + .card { margin-top: 10px; }
  .card h4 {
    font-size: 11px; color: var(--muted);
    text-transform: uppercase; letter-spacing: 0.08em;
    margin-bottom: 8px;
  }
  .card p { font-size: 13px; line-height: 1.45; color: var(--text); }
  .grid-2 { display: grid; grid-template-columns: 1.2fr 1fr; gap: 10px; }
  .row {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; font-size: 12.5px; padding: 8px 0;
    border-top: 1px solid rgba(255,255,255,0.07);
  }
  .row:first-of-type { border-top: 0; }
  .row strong { color: var(--text); font-size: 13px; }
  .badge {
    font-size: 10px; border-radius: 999px;
    padding: 2px 9px; border: 1px solid transparent; white-space: nowrap;
  }
  .b-danger  { background: rgba(248,113,113,0.12); border-color: rgba(248,113,113,0.35); color: #fecaca; }
  .b-warn    { background: rgba(245,158,11,0.12);  border-color: rgba(245,158,11,0.35);  color: #fde68a; }
  .b-accent  { background: rgba(45,212,191,0.12);  border-color: rgba(45,212,191,0.35);  color: #b8fff3; }
  .b-muted   { background: rgba(148,163,184,0.10); border-color: rgba(148,163,184,0.30); color: #cbd5e1; }
  .b-indigo  { background: rgba(165,180,252,0.10); border-color: rgba(165,180,252,0.30); color: #c7d2fe; }

  /* BUILD - kanban */
  .kanban {
    display: grid; grid-template-columns: repeat(4, minmax(0,1fr));
    gap: 10px; height: calc(100% - 70px);
  }
  .col {
    border: 1px solid var(--line); border-radius: 12px;
    background: #0f1623; padding: 10px;
    display: flex; flex-direction: column; gap: 8px;
    min-height: 0; overflow: hidden;
  }
  .col-h {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 11px; color: var(--muted);
    text-transform: uppercase; letter-spacing: 0.08em;
  }
  .col-count {
    font-size: 10px; color: var(--text);
    background: rgba(255,255,255,0.06); padding: 1px 7px; border-radius: 999px;
  }
  .ticket {
    border: 1px solid var(--line); border-radius: 8px;
    background: #131d2c; padding: 9px 10px;
  }
  .ticket-id { font-size: 9.5px; color: var(--muted); letter-spacing: 0.06em; }
  .ticket-t  { font-size: 12.5px; margin-top: 2px; line-height: 1.3; }
  .ticket-meta {
    margin-top: 6px; display: flex; justify-content: space-between;
    align-items: center; gap: 6px; font-size: 10px; color: var(--muted);
  }
  .av {
    width: 18px; height: 18px; border-radius: 50%;
    background: linear-gradient(135deg,#5eead4,#a5b4fc);
    font-size: 9px; color: #04211e; font-weight: 700;
    display: inline-flex; align-items: center; justify-content: center;
  }

  /* MONEY - chart */
  .chart {
    height: 130px; margin-top: 4px;
    background: linear-gradient(180deg, rgba(45,212,191,0.12), rgba(45,212,191,0));
    border-radius: 10px; position: relative; overflow: hidden;
  }
  .chart svg { position: absolute; inset: 0; width: 100%; height: 100%; }
  .ledger { font-size: 12.5px; }
  .ledger .row span:first-child { color: var(--muted); }

  /* TEAM - capacity bars */
  .bar { height: 6px; border-radius: 999px; background: rgba(255,255,255,0.07); overflow: hidden; }
  .bar > div { height: 100%; border-radius: 999px; }
  .person {
    display: grid; grid-template-columns: 130px 1fr 56px;
    gap: 12px; align-items: center; padding: 9px 0;
    border-top: 1px solid rgba(255,255,255,0.07);
    font-size: 12.5px;
  }
  .person:first-child { border-top: 0; }
  .person > div:first-child { display: flex; align-items: center; gap: 8px; }
  .pct { text-align: right; font-size: 12px; color: var(--muted); }

  /* Narration overlay */
  .narrate {
    position: absolute; left: 14px; right: 14px; bottom: 12px;
    border: 1px solid rgba(45,212,191,0.26);
    background: rgba(7,15,25,0.93);
    border-radius: 10px; padding: 10px 12px;
    backdrop-filter: blur(6px);
    z-index: 5;
  }
  .narrate-k { color: #5eead4; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; }
  .narrate-t { margin-top: 2px; font-size: 14px; font-weight: 600; }
  .narrate-c { margin-top: 3px; color: var(--muted); font-size: 12px; line-height: 1.35; }
  .progress { margin-top: 8px; height: 3px; background: rgba(255,255,255,0.07); border-radius: 999px; overflow: hidden; }
  .progress > div { height: 100%; background: linear-gradient(90deg, var(--accent), #5eead4); width: 0%; }

  .frame::after {
    content: ""; position: absolute; left: 0; right: 0; bottom: 0;
    height: 110px;
    background: linear-gradient(0deg, rgba(15,23,36,0.95), rgba(15,23,36,0));
    z-index: 3; pointer-events: none;
  }

  /* Metadata pill row + action chips (Today AI brief) */
  .pills { display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 10px; }
  .acts { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px; }
  .act {
    font-size: 11px; border-radius: 8px; padding: 6px 11px;
    border: 1px solid rgba(45,212,191,0.32);
    background: rgba(45,212,191,0.10); color: #7ff5e7; white-space: nowrap;
  }

  /* Weekly momentum bar chart (Today) */
  .mo { display: flex; align-items: flex-end; gap: 8px; height: 96px; padding: 6px 2px 4px; }
  .mo-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; height: 100%; justify-content: flex-end; }
  .mo-bar { width: 100%; border-radius: 6px 6px 0 0; background: rgba(255,255,255,0.08); }
  .mo-bar.fill { background: linear-gradient(180deg,#34d399,#0f8a83); }
  .mo-bar.today { background: linear-gradient(180deg,#5eead4,var(--accent)); }
  .mo-d { font-size: 9.5px; color: var(--muted); }

  /* Subtab pill bar (Build / AI / Team) */
  .subtabs {
    display: flex; gap: 4px; padding: 4px; margin-bottom: 12px;
    background: rgba(255,255,255,0.04); border: 1px solid var(--line);
    border-radius: 10px; width: max-content; max-width: 100%;
  }
  .subtab {
    font-size: 11.5px; padding: 5px 12px; border-radius: 7px;
    color: var(--muted); white-space: nowrap;
  }
  .subtab.on {
    background: var(--accent-soft); color: var(--accent);
    border: 1px solid rgba(45,212,191,0.30);
  }

  /* AI chat */
  .chat { display: flex; flex-direction: column; gap: 10px; }
  .bubble { max-width: 78%; padding: 10px 12px; border-radius: 12px; font-size: 12.5px; line-height: 1.5; }
  .bubble.me {
    align-self: flex-end; background: var(--accent-soft);
    border: 1px solid rgba(45,212,191,0.30); color: var(--text);
    border-bottom-right-radius: 4px;
  }
  .bubble.ai {
    align-self: flex-start; background: #111a28; border: 1px solid var(--line);
    border-bottom-left-radius: 4px;
  }
  .bubble-k { font-size: 9.5px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
  .prompts { display: flex; flex-wrap: wrap; gap: 7px; margin: 12px 0; }
  .prompt {
    font-size: 11px; padding: 6px 11px; border-radius: 99px;
    border: 1px solid var(--line-strong); color: var(--muted); white-space: nowrap;
  }
  .composer {
    display: flex; align-items: center; gap: 8px;
    border: 1px solid var(--line-strong); border-radius: 12px;
    background: #111a28; padding: 8px 8px 8px 14px;
  }
  .composer .ph { flex: 1; font-size: 12.5px; color: var(--muted); }
  .send {
    width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
    background: var(--accent); color: #04211e;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 15px; font-weight: 700;
  }

  /* ───────────────────────────────────────── LIGHT THEME OVERRIDES ───────────────────────────────────────── */
  html[data-theme="light"] {
    --bg: #f7f8fa;
    --surface: #ffffff;
    --panel: #ffffff;
    --text: #0f172a;
    --muted: #64748b;
    --line: rgba(15,23,42,0.08);
    --line-strong: rgba(15,23,42,0.14);
    --accent: #0d9488;
    --accent-soft: rgba(13,148,136,0.10);
    --warn: #b45309;
    --danger: #b91c1c;
    --good: #047857;
    --indigo: #4f46e5;
  }
  html[data-theme="light"] body { background: var(--bg); color: var(--text); }
  html[data-theme="light"] .side { background: #ffffff; border-right-color: var(--line); }
  html[data-theme="light"] .nav-item { color: var(--muted); }
  html[data-theme="light"] .nav-item.is-active {
    color: var(--accent);
    border-color: rgba(13,148,136,0.32);
    background: rgba(13,148,136,0.08);
  }
  html[data-theme="light"] .frame {
    background: #ffffff;
    border-color: var(--line);
  }
  html[data-theme="light"] .metric,
  html[data-theme="light"] .card,
  html[data-theme="light"] .col,
  html[data-theme="light"] .ticket {
    background: #ffffff;
    border-color: var(--line);
  }
  html[data-theme="light"] .col { background: #f8fafc; }
  html[data-theme="light"] .ticket { background: #ffffff; box-shadow: 0 1px 2px rgba(15,23,42,0.04); }
  html[data-theme="light"] .row { border-top-color: var(--line); }
  html[data-theme="light"] .col-count {
    color: var(--text);
    background: rgba(15,23,42,0.06);
  }
  html[data-theme="light"] .chip {
    border-color: rgba(13,148,136,0.32);
    background: rgba(13,148,136,0.08);
    color: #0f766e;
  }
  html[data-theme="light"] .b-danger  { background: rgba(185,28,28,0.08);  border-color: rgba(185,28,28,0.30);  color: #991b1b; }
  html[data-theme="light"] .b-warn    { background: rgba(180,83,9,0.10);   border-color: rgba(180,83,9,0.30);   color: #92400e; }
  html[data-theme="light"] .b-accent  { background: rgba(13,148,136,0.10); border-color: rgba(13,148,136,0.30); color: #0f766e; }
  html[data-theme="light"] .b-muted   { background: rgba(100,116,139,0.10);border-color: rgba(100,116,139,0.30);color: #475569; }
  html[data-theme="light"] .b-indigo  { background: rgba(79,70,229,0.10);  border-color: rgba(79,70,229,0.30);  color: #3730a3; }
  html[data-theme="light"] .bar { background: rgba(15,23,42,0.08); }
  html[data-theme="light"] .person { border-top-color: var(--line); }
  html[data-theme="light"] .narrate {
    background: rgba(255,255,255,0.96);
    border-color: rgba(13,148,136,0.30);
    color: var(--text);
    box-shadow: 0 6px 22px rgba(15,23,42,0.08);
  }
  html[data-theme="light"] .narrate-k { color: #0f766e; }
  html[data-theme="light"] .narrate-c { color: var(--muted); }
  html[data-theme="light"] .progress { background: rgba(15,23,42,0.08); }
  html[data-theme="light"] .progress > div { background: linear-gradient(90deg, var(--accent), #14b8a6); }
  html[data-theme="light"] .frame::after {
    background: linear-gradient(0deg, rgba(255,255,255,0.95), rgba(255,255,255,0));
  }
  html[data-theme="light"] .ledger .row span:first-child { color: var(--muted); }
  /* Re-tint the inline-styled metric numbers so they read on white */
  html[data-theme="light"] .metric-v[style*="#34d399"] { color: #047857 !important; }
  html[data-theme="light"] .metric-v[style*="#fcd34d"] { color: #b45309 !important; }
  html[data-theme="light"] .metric-v[style*="#a5b4fc"] { color: #4f46e5 !important; }
  html[data-theme="light"] .metric-v[style*="#f87171"] { color: #b91c1c !important; }
  html[data-theme="light"] .metric-trend[style*="#fcd34d"] { color: #b45309 !important; }
  html[data-theme="light"] .metric-trend[style*="#f87171"] { color: #b91c1c !important; }
  html[data-theme="light"] .ticket[style*="rgba(248,113,113"] { border-color: rgba(185,28,28,0.40) !important; }
  html[data-theme="light"] .chart {
    background: linear-gradient(180deg, rgba(13,148,136,0.10), rgba(13,148,136,0));
  }
  html[data-theme="light"] .act { border-color: rgba(13,148,136,0.32); background: rgba(13,148,136,0.10); color: #0f766e; }
  html[data-theme="light"] .mo-bar { background: rgba(15,23,42,0.08); }
  html[data-theme="light"] .subtabs { background: rgba(15,23,42,0.03); border-color: var(--line); }
  html[data-theme="light"] .subtab.on { border-color: rgba(13,148,136,0.30); }
  html[data-theme="light"] .bubble.ai { background: #f8fafc; border-color: var(--line); box-shadow: 0 1px 2px rgba(15,23,42,0.04); }
  html[data-theme="light"] .bubble.me { border-color: rgba(13,148,136,0.30); }
  html[data-theme="light"] .composer { background: #f8fafc; border-color: var(--line-strong); }
  html[data-theme="light"] .prompt { border-color: var(--line-strong); }
  html[data-theme="light"] .send { color: #ffffff; }

  /* ─── MOBILE: sidebar → top tab strip ─────────────────────── */
  @media (max-width: 560px) {
    .shell {
      grid-template-columns: 1fr;
      grid-template-rows: 44px 1fr;
    }
    .side {
      flex-direction: row;
      align-items: center;
      padding: 0 10px;
      gap: 3px;
      border-right: 0;
      border-bottom: 1px solid var(--line);
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }
    .side::-webkit-scrollbar { display: none; }
    .logo {
      width: 26px; height: 26px; font-size: 11px;
      border-radius: 7px; flex-shrink: 0; margin-right: 4px;
    }
    .nav-item {
      width: auto; padding: 4px 9px; margin-top: 0;
      white-space: nowrap; font-size: 8.5px; border-radius: 7px; flex-shrink: 0;
    }
    .content { padding: 8px; }
    .frame { border-radius: 10px; }
    .view { padding: 10px 11px 80px; }
    .top { padding-bottom: 9px; margin-bottom: 10px; }
    .title { font-size: 17px; }
    .sub { font-size: 9.5px; }
    .chip { font-size: 9.5px; padding: 4px 8px; }
    .metrics {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px; margin-bottom: 9px;
    }
    .metric { padding: 7px 8px; min-height: 50px; }
    .metric-k { font-size: 9px; }
    .metric-v { font-size: 17px; margin-top: 3px; }
    .metric-trend { font-size: 9px; }
    .grid-2 { grid-template-columns: 1fr; gap: 6px; }
    .card { padding: 9px 10px; }
    .card h4 { font-size: 9.5px; margin-bottom: 6px; }
    .card p { font-size: 11.5px; }
    .row { font-size: 11.5px; padding: 6px 0; }
    .row strong { font-size: 11.5px; }
    .badge { font-size: 9px; padding: 2px 7px; }
    .section-h { font-size: 9.5px; margin: 10px 0 6px; }
    /* Kanban: show only In Progress + Done */
    .kanban {
      grid-template-columns: 1fr 1fr;
      height: calc(100% - 65px);
      gap: 7px;
    }
    .col:nth-child(1),
    .col:nth-child(3) { display: none; }
    .col { padding: 8px; gap: 6px; }
    .col-h { font-size: 9.5px; }
    .ticket { padding: 7px 8px; border-radius: 7px; }
    .ticket-id { font-size: 8.5px; }
    .ticket-t { font-size: 11px; }
    .ticket-meta { font-size: 9px; }
    .av { width: 15px; height: 15px; font-size: 8px; }
    .chart { height: 85px; }
    .person {
      grid-template-columns: 80px 1fr 36px;
      gap: 8px; font-size: 11.5px; padding: 7px 0;
    }
    .pct { font-size: 10.5px; }
    .bar { height: 5px; }
    .narrate {
      left: 8px; right: 8px; bottom: 8px; padding: 8px 10px; border-radius: 8px;
    }
    .narrate-k { font-size: 9px; }
    .narrate-t { font-size: 12px; margin-top: 2px; }
    .narrate-c { font-size: 10.5px; margin-top: 2px; }
    .progress { margin-top: 6px; }
    .frame::after { height: 80px; }
    .mo { height: 66px; }
    .subtabs { margin-bottom: 9px; }
    .subtab { font-size: 10px; padding: 4px 9px; }
    .bubble { max-width: 88%; font-size: 11.5px; }
    .prompt { font-size: 10px; padding: 5px 9px; }
    .composer .ph { font-size: 11.5px; }
  }
</style>
</head>
<body>
  <div class="shell">
    <aside class="side">
      <div class="nav-item is-active" data-nav="today">Today</div>
      <div class="nav-item" data-nav="build">Build</div>
      <div class="nav-item" data-nav="ai">AI</div>
      <div class="nav-item" data-nav="company">Money</div>
      <div class="nav-item" data-nav="team">Team</div>
    </aside>

    <main class="content">
      <section class="frame">

        <!-- TODAY -->
        <div class="view is-active" data-view="today">
          <div class="top">
            <div>
              <div class="title">Good morning, Founder</div>
              <div class="sub">Tuesday, April 21 &middot; 3 priorities &middot; 6 days to milestone</div>
            </div>
            <div class="chip">Today</div>
          </div>

          <div class="card">
            <h4>AI daily brief</h4>
            <div class="pills">
              <span class="badge b-indigo">14 open issues</span>
              <span class="badge b-danger">$42K &middot; 3.1mo runway</span>
              <span class="badge b-accent">4 team members</span>
              <span class="badge b-warn">Beta launch &middot; 6d</span>
            </div>
            <p>Runway is below 4 months. Ship the auth token refresh today to unblock beta launch and near-term MRR. Delegate the investor draft to Rahul and protect a 90-min focus block this morning.</p>
            <div class="acts">
              <span class="act">Create sprint goals</span>
              <span class="act">View priorities</span>
              <span class="act">Open Money</span>
            </div>
          </div>

          <div class="section-h">Top 3 priorities</div>
          <div class="card">
            <div class="row"><span>&#128308; Auth token refresh bug</span><span class="badge b-danger">Blocker</span></div>
            <div class="row"><span>&#128992; Onboarding UI polish</span><span class="badge b-warn">High</span></div>
            <div class="row"><span>&#128993; Stripe webhook handler</span><span class="badge b-accent">Medium</span></div>
          </div>

          <div class="grid-2">
            <div class="card">
              <h4>Weekly momentum</h4>
              <div class="mo">
                <div class="mo-col"><div class="mo-bar fill" style="height:40%"></div><div class="mo-d">M</div></div>
                <div class="mo-col"><div class="mo-bar fill" style="height:62%"></div><div class="mo-d">T</div></div>
                <div class="mo-col"><div class="mo-bar fill" style="height:48%"></div><div class="mo-d">W</div></div>
                <div class="mo-col"><div class="mo-bar fill" style="height:75%"></div><div class="mo-d">T</div></div>
                <div class="mo-col"><div class="mo-bar today" style="height:90%"></div><div class="mo-d">F</div></div>
                <div class="mo-col"><div class="mo-bar" style="height:18%"></div><div class="mo-d">S</div></div>
                <div class="mo-col"><div class="mo-bar" style="height:18%"></div><div class="mo-d">S</div></div>
              </div>
              <div class="row"><span>This week</span><span class="badge b-accent">73% &middot; &#9650; 6</span></div>
            </div>
            <div class="card">
              <h4>Focus block</h4>
              <div class="row"><span>Current task</span><strong>Auth token refresh</strong></div>
              <div class="row"><span>Elapsed</span><span class="badge b-accent">32:14 / 90:00</span></div>
              <div class="progress" style="margin-top:8px;"><div style="width:36%"></div></div>
              <div class="row"><span>State</span><span class="badge b-warn">In progress</span></div>
            </div>
          </div>

          <div class="section-h">Today's schedule</div>
          <div class="card">
            <div class="row"><span>09:00 &middot; Focus &mdash; Auth fix</span><span class="badge b-accent">90m</span></div>
            <div class="row"><span>11:00 &middot; Standup</span><span class="badge b-muted">15m</span></div>
            <div class="row"><span>14:00 &middot; Design partner call</span><span class="badge b-indigo">30m</span></div>
          </div>
        </div>

        <!-- BUILD -->
        <div class="view" data-view="build">
          <div class="top">
            <div>
              <div class="title">Build</div>
              <div class="sub">Sprint 14 &middot; 4 in flight &middot; 2 ready for review &middot; milestone in 6d</div>
            </div>
            <div class="chip">Beta launch &middot; 6d</div>
          </div>

          <div class="metrics" style="grid-template-columns:repeat(5,minmax(0,1fr));">
            <div class="metric"><div class="metric-k">Total</div><div class="metric-v">26</div></div>
            <div class="metric"><div class="metric-k">In progress</div><div class="metric-v" style="color:#34d399;">3</div></div>
            <div class="metric"><div class="metric-k">Done</div><div class="metric-v">9</div></div>
            <div class="metric"><div class="metric-k">Overdue</div><div class="metric-v" style="color:#f87171;">2</div></div>
            <div class="metric"><div class="metric-k">Complete</div><div class="metric-v" style="color:#a5b4fc;">42%</div></div>
          </div>

          <div class="subtabs">
            <div class="subtab on">Board</div>
            <div class="subtab">Priorities</div>
            <div class="subtab">Milestones</div>
          </div>

          <div class="kanban">
            <div class="col">
              <div class="col-h"><span>To Do</span><span class="col-count">12</span></div>
              <div class="ticket"><div class="ticket-id">BUG-204</div><div class="ticket-t">Empty workspace edge case</div><div class="ticket-meta"><span class="badge b-muted">Low</span><span class="av">M</span></div></div>
              <div class="ticket"><div class="ticket-id">FEAT-118</div><div class="ticket-t">Investor digest email</div><div class="ticket-meta"><span class="badge b-muted">Low</span><span class="av">R</span></div></div>
              <div class="ticket"><div class="ticket-id">FEAT-121</div><div class="ticket-t">Mobile swipe between tabs</div><div class="ticket-meta"><span class="badge b-muted">Low</span><span class="av">S</span></div></div>
            </div>
            <div class="col">
              <div class="col-h"><span>In Progress</span><span class="col-count">3</span></div>
              <div class="ticket" style="border-color:rgba(248,113,113,0.35);">
                <div class="ticket-id">BUG-198</div>
                <div class="ticket-t">Auth token refresh on tab focus</div>
                <div class="ticket-meta"><span class="badge b-danger">Blocker</span><span class="av">S</span></div>
              </div>
              <div class="ticket"><div class="ticket-id">FEAT-114</div><div class="ticket-t">Onboarding step 3 polish</div><div class="ticket-meta"><span class="badge b-warn">High</span><span class="av">M</span></div></div>
              <div class="ticket"><div class="ticket-id">FEAT-116</div><div class="ticket-t">Stripe webhook handler</div><div class="ticket-meta"><span class="badge b-accent">Med</span><span class="av">J</span></div></div>
            </div>
            <div class="col">
              <div class="col-h"><span>In Review</span><span class="col-count">2</span></div>
              <div class="ticket"><div class="ticket-id">FEAT-112</div><div class="ticket-t">Daily brief AI summarizer</div><div class="ticket-meta"><span class="badge b-accent">Ready</span><span class="av">M</span></div></div>
              <div class="ticket"><div class="ticket-id">FEAT-113</div><div class="ticket-t">Capacity rebalance suggestions</div><div class="ticket-meta"><span class="badge b-accent">Ready</span><span class="av">R</span></div></div>
            </div>
            <div class="col">
              <div class="col-h"><span>Done</span><span class="col-count">9</span></div>
              <div class="ticket"><div class="ticket-id">FEAT-110</div><div class="ticket-t">Cross-tool signals widget</div><div class="ticket-meta"><span class="badge b-muted">Shipped</span><span class="av">S</span></div></div>
              <div class="ticket"><div class="ticket-id">FEAT-109</div><div class="ticket-t">Focus block timer</div><div class="ticket-meta"><span class="badge b-muted">Shipped</span><span class="av">M</span></div></div>
              <div class="ticket"><div class="ticket-id">FEAT-107</div><div class="ticket-t">Runway alert thresholds</div><div class="ticket-meta"><span class="badge b-muted">Shipped</span><span class="av">J</span></div></div>
            </div>
          </div>
        </div>

        <!-- AI -->
        <div class="view" data-view="ai">
          <div class="top">
            <div>
              <div class="title">AI Co-Founder</div>
              <div class="sub">Context-aware &middot; sees your runway, milestones &amp; build progress</div>
            </div>
            <div class="chip">Context-aware</div>
          </div>

          <div class="subtabs">
            <div class="subtab on">Strategy</div>
            <div class="subtab">Funding</div>
            <div class="subtab">Growth</div>
            <div class="subtab">Technical</div>
          </div>

          <div class="chat">
            <div class="bubble me">With 3.1 months of runway and beta in 6 days, should I push the auth fix or start raising now?</div>
            <div class="bubble ai">
              <div class="bubble-k">AI Co-Founder &middot; sees your data</div>
              Ship the auth fix first &mdash; it&rsquo;s the only blocker between you and beta. Beta opens trials, which buys runway evidence. Warm 5 investors in parallel this week, but don&rsquo;t open a formal raise until you have 2 weeks of beta signal.
            </div>
            <div class="bubble me">Who should I delegate the investor update to?</div>
          </div>

          <div class="prompts">
            <span class="prompt">What should I build first?</span>
            <span class="prompt">Do I have PMF?</span>
            <span class="prompt">Plan my next 2 weeks</span>
          </div>

          <div class="composer">
            <div class="ph">Ask your co-founder anything&hellip;</div>
            <div class="send">&#8593;</div>
          </div>
        </div>

        <!-- COMPANY -->
        <div class="view" data-view="company">
          <div class="top">
            <div>
              <div class="title">Money</div>
              <div class="sub">$42K cash &middot; $13.5K monthly burn &middot; 3.1mo runway &middot; MRR $899 &middot; 4 decisions logged</div>
            </div>
            <div class="chip" style="border-color:rgba(248,113,113,0.35); background:rgba(248,113,113,0.10); color:#fecaca;">Runway alert &middot; under 4mo</div>
          </div>

          <div class="metrics">
            <div class="metric"><div class="metric-k">Cash on hand</div><div class="metric-v">$42K</div><div class="metric-trend" style="color:var(--muted);">As of today</div></div>
            <div class="metric"><div class="metric-k">Monthly burn</div><div class="metric-v" style="color:#fcd34d;">$13.5K</div><div class="metric-trend" style="color:#fcd34d;">&#9650; $0.6K vs last mo</div></div>
            <div class="metric"><div class="metric-k">MRR</div><div class="metric-v" style="color:#34d399;">$899</div><div class="metric-trend">&#9650; 22% MoM</div></div>
            <div class="metric"><div class="metric-k">Runway</div><div class="metric-v" style="color:#fcd34d;">3.1 mo</div><div class="metric-trend" style="color:#fcd34d;">&#9888; tighten</div></div>
          </div>

          <div class="grid-2">
            <div class="card">
              <h4>Cash Trajectory &middot; 6 months</h4>
              <div class="chart">
                <svg viewBox="0 0 300 130" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stop-color="#2dd4bf" stop-opacity="0.55"/>
                      <stop offset="100%" stop-color="#2dd4bf" stop-opacity="0"/>
                    </linearGradient>
                  </defs>
                  <path d="M0,30 L50,40 L100,55 L150,72 L200,90 L250,108 L300,122 L300,130 L0,130 Z" fill="url(#g)"/>
                  <path d="M0,30 L50,40 L100,55 L150,72 L200,90 L250,108 L300,122" stroke="#2dd4bf" stroke-width="2" fill="none"/>
                  <line x1="0" y1="100" x2="300" y2="100" stroke="rgba(248,113,113,0.5)" stroke-dasharray="4 4" stroke-width="1"/>
                </svg>
              </div>
              <div class="row"><span>Projected zero-cash</span><span class="badge b-danger">Jul 28</span></div>
              <div class="row"><span>Recommended action</span><span class="badge b-accent">Ship beta &middot; open trials</span></div>
            </div>
            <div class="card ledger">
              <h4>Recent Activity</h4>
              <div class="row"><span>Stripe payouts</span><strong style="color:#34d399;">+$899</strong></div>
              <div class="row"><span>AWS &middot; April</span><strong>&minus;$1,840</strong></div>
              <div class="row"><span>Payroll &middot; April</span><strong>&minus;$9,200</strong></div>
              <div class="row"><span>OpenAI API</span><strong>&minus;$420</strong></div>
              <div class="row"><span>Tools &amp; SaaS</span><strong>&minus;$1,140</strong></div>
            </div>
          </div>

          <div class="section-h">Decisions &amp; Stakeholders</div>
          <div class="grid-2">
            <div class="card">
              <h4>Recent Decisions</h4>
              <div class="row"><span>Ship auth fix before raise</span><span class="badge b-accent">Apr 21</span></div>
              <div class="row"><span>Pause paid acquisition</span><span class="badge b-warn">Apr 18</span></div>
              <div class="row"><span>Pricing: $29 starter tier</span><span class="badge b-indigo">Apr 14</span></div>
              <div class="row"><span>Hire ops contractor</span><span class="badge b-muted">Apr 09</span></div>
            </div>
            <div class="card">
              <h4>Investor Pipeline</h4>
              <div class="row"><span>Sequoia Scout · Priya</span><span class="badge b-accent">Warm</span></div>
              <div class="row"><span>South Park Commons</span><span class="badge b-warn">2nd call</span></div>
              <div class="row"><span>Hustle Fund</span><span class="badge b-indigo">Intro’d</span></div>
              <div class="row"><span>Angel · D. Patel</span><span class="badge b-accent">Soft commit</span></div>
            </div>
          </div>
        </div>

        <!-- TEAM -->
        <div class="view" data-view="team">
          <div class="top">
            <div>
              <div class="title">Team</div>
              <div class="sub">4 members &middot; avg load 60% &middot; 1 over capacity</div>
            </div>
            <div class="chip">Capacity check</div>
          </div>

          <div class="subtabs">
            <div class="subtab on">Workload</div>
            <div class="subtab">Objectives</div>
            <div class="subtab">Chat</div>
          </div>

          <div class="grid-2">
            <div class="card">
              <h4>Team workload</h4>
              <div class="person">
                <div><span class="av" style="background:linear-gradient(135deg,#f87171,#fb7185); color:#fff;">Y</span> You &middot; Founder</div>
                <div class="bar"><div style="width:86%; background:#f87171;"></div></div>
                <div class="pct">86%</div>
              </div>
              <div class="person">
                <div><span class="av">M</span> Maya &middot; Eng</div>
                <div class="bar"><div style="width:52%; background:#2dd4bf;"></div></div>
                <div class="pct">52%</div>
              </div>
              <div class="person">
                <div><span class="av" style="background:linear-gradient(135deg,#fde68a,#f59e0b);">J</span> James &middot; Design</div>
                <div class="bar"><div style="width:70%; background:#f59e0b;"></div></div>
                <div class="pct">70%</div>
              </div>
              <div class="person">
                <div><span class="av" style="background:linear-gradient(135deg,#a5b4fc,#818cf8); color:#fff;">R</span> Rahul &middot; Ops</div>
                <div class="bar"><div style="width:30%; background:#2dd4bf;"></div></div>
                <div class="pct">30%</div>
              </div>
            </div>
            <div class="card">
              <h4>Objectives</h4>
              <div class="row"><span>Ship public beta</span><span class="badge b-accent">72%</span></div>
              <div class="progress" style="margin-top:2px;"><div style="width:72%"></div></div>
              <div class="row"><span>Reach $2K MRR</span><span class="badge b-warn">45%</span></div>
              <div class="progress" style="margin-top:2px;"><div style="width:45%"></div></div>
              <div class="row"><span>Close pre-seed round</span><span class="badge b-indigo">20%</span></div>
              <div class="progress" style="margin-top:2px;"><div style="width:20%"></div></div>
              <h4 style="margin-top:14px;">Active threads</h4>
              <div class="row"><span>#beta-launch</span><span class="badge b-muted">2 new</span></div>
              <div class="row"><span>#auth-bug</span><span class="badge b-danger">live</span></div>
              <div class="row"><span>DM &middot; Maya</span><span class="badge b-accent">resolved</span></div>
            </div>
          </div>
        </div>

        <!-- Narration overlay -->
        <div class="narrate">
          <div class="narrate-k" id="narrateK">Tab 1 of 5 &middot; Today</div>
          <div class="narrate-t" id="narrateT">Today &mdash; your morning command center</div>
          <div class="narrate-c" id="narrateC">AI brief, top priorities, and a focus block. The signal you need before opening email or Slack.</div>
          <div class="progress"><div id="progressBar"></div></div>
        </div>

      </section>
    </main>
  </div>

<script>
(function () {
  var navItems = document.querySelectorAll('.nav-item');
  var views = document.querySelectorAll('.view');
  var narrateK = document.getElementById('narrateK');
  var narrateT = document.getElementById('narrateT');
  var narrateC = document.getElementById('narrateC');
  var progressBar = document.getElementById('progressBar');

  var tabs = [
    { key: 'today', label: 'Today',
      title: 'Today \u2014 your morning command center',
      copy: 'AI daily brief, your top 3 priorities, weekly momentum and a focus block \u2014 the signal before you open Slack.',
      duration: 5000 },
    { key: 'build', label: 'Build',
      title: 'Build \u2014 execution board',
      copy: 'Stats, board, priorities and milestones \u2014 every issue, sprint and blocker in one execution view.',
      duration: 5200 },
    { key: 'ai', label: 'AI',
      title: 'AI \u2014 your context-aware co-founder',
      copy: 'Knows your runway, milestones, and team. Ask anything; it answers with your data, not generic advice.',
      duration: 5400 },
    { key: 'company', label: 'Money',
      title: 'Money \u2014 runway, burn & investors',
      copy: 'Cash, runway, MRR, expenses and the investor pipeline that shape your next milestone.',
      duration: 5400 },
    { key: 'team', label: 'Team',
      title: 'Team \u2014 workload & objectives',
      copy: 'See who is overloaded, track objectives, and rebalance work \u2014 team chat included.',
      duration: 5000 },
  ];

  function show(key) {
    navItems.forEach(function (n) { n.classList.toggle('is-active', n.dataset.nav === key); });
    views.forEach(function (v) { v.classList.toggle('is-active', v.dataset.view === key); });
  }

  var i = 0;
  function play() {
    var t = tabs[i];
    show(t.key);
    narrateK.textContent = 'Tab ' + (i + 1) + ' of ' + tabs.length + ' \u00b7 ' + t.label;
    narrateT.textContent = t.title;
    narrateC.textContent = t.copy;

    progressBar.style.transition = 'none';
    progressBar.style.width = '0%';
    requestAnimationFrame(function () {
      progressBar.style.transition = 'width ' + t.duration + 'ms linear';
      progressBar.style.width = '100%';
    });

    setTimeout(function () {
      i = (i + 1) % tabs.length;
      play();
    }, t.duration);
  }

  setTimeout(play, 300);
})();
</script>
</body>
</html>`;

export default productPreviewHtml;

// Light-themed variant for the public login/register page.
// We just stamp `data-theme="light"` on <html>; the CSS above does the rest.
export const productPreviewHtmlLight = productPreviewHtml.replace(
  '<html lang="en">',
  '<html lang="en" data-theme="light">'
);
